/**
 * Upcoming Events Snapshot Component
 * Merged component replacing "Upcoming Events" and "Operations Snapshot"
 * 
 * Shows metrics for next 7 days:
 * - Check-ins (count)
 * - Check-outs (count)
 * 
 * No detailed event listings, no gaps
 * Compact layout: title left, metrics right (single row)
 * 
 * NOTE: Occupancy metric removed per requirement
 */

export default function UpcomingEventsSnapshot({ snapshot = null, loading = false, error = null, isMobile = false }) {
  // Extract values from snapshot (defaults to 0 if not provided)
  const checkIns = snapshot?.checkins ?? 0;
  const checkOuts = snapshot?.checkouts ?? 0;

  return (
    <div className="owner-section-panel" data-component="upcoming-events">
      {/* Compact header: title left, metrics right */}
      <div style={isMobile ? styles.getHeaderRowStyleMobile() : styles.headerRow}>
        <h3 className="owner-section-panel-title" style={isMobile ? styles.getTitleStyleMobile() : styles.title}>Upcoming Events (next 7 days)</h3>
        <div style={isMobile ? styles.getMetricsClusterStyleMobile() : styles.metricsCluster}>
          {loading ? (
            <>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>...</span>
                <span style={styles.metricLabel}>Check-ins</span>
              </div>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>...</span>
                <span style={styles.metricLabel}>Check-outs</span>
              </div>
            </>
          ) : error ? (
            <>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>—</span>
                <span style={styles.metricLabel}>Check-ins</span>
              </div>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>—</span>
                <span style={styles.metricLabel}>Check-outs</span>
              </div>
            </>
          ) : (
            <>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>{checkIns}</span>
                <span style={styles.metricLabel}>Check-ins</span>
              </div>
              <div style={isMobile ? styles.getMetricItemStyleMobile() : styles.metricItem}>
                <span style={isMobile ? styles.getMetricCountStyleMobile() : styles.metricCount}>{checkOuts}</span>
                <span style={styles.metricLabel}>Check-outs</span>
              </div>
            </>
          )}
        </div>
      </div>
      {/* BEYOND WORLD-CLASS: Trust-grade status notice for error state */}
      {error && (
        <div style={isMobile ? styles.getStatusNoticeStyleMobile() : styles.statusNotice}>
          <div style={styles.statusNoticeHeader}>
            <span style={styles.statusNoticeIcon}>⚠️</span>
            <span style={styles.statusNoticeTitle}>Upcoming events unavailable</span>
          </div>
          <div style={styles.statusNoticeBody}>
            <p style={styles.statusNoticeText}>
              Check-in and check-out counts for the next 7 days couldn't be loaded. Your calendar and bookings are still visible.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={styles.statusNoticeAction}
              aria-label="Retry loading upcoming events"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-4)',
    padding: 'var(--spacing-3) var(--spacing-4)', // Reduced top/bottom padding
    flexWrap: 'wrap',
  },
  // PHASE 12: Mobile header row - compact
  getHeaderRowStyleMobile: () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)', // PHASE 12: Tighter gap (8px)
    padding: '6px var(--spacing-3)', // PHASE 12: Reduced padding (6px vertical, 12px horizontal)
    flexWrap: 'wrap',
  }),
  title: {
    flex: '0 0 auto',
    margin: 0,
  },
  // PHASE 12: Mobile title - smaller
  getTitleStyleMobile: () => ({
    flex: '0 0 auto',
    margin: 0,
    fontSize: 'var(--font-size-sm)', // PHASE 12: Smaller header
    fontWeight: 'var(--font-weight-medium)', // PHASE 12: Medium weight
  }),
  metricsCluster: {
    display: 'flex',
    gap: 'var(--spacing-6)',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: '0 0 auto',
  },
  // PHASE 12: Mobile metrics cluster - compact
  getMetricsClusterStyleMobile: () => ({
    display: 'flex',
    gap: 'var(--spacing-3)', // PHASE 12: Tighter gap (12px, was 24px)
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: '0 0 auto',
  }),
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    alignItems: 'center',
    textAlign: 'center',
    minWidth: '60px',
  },
  // PHASE 12: Mobile metric item - compact
  getMetricItemStyleMobile: () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    alignItems: 'center',
    textAlign: 'center',
    minWidth: '50px', // PHASE 12: Smaller min-width
  }),
  metricCount: {
    fontSize: 'var(--font-size-xl, 1.25rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    lineHeight: '1.2',
  },
  // PHASE 12: Mobile metric count - smaller
  getMetricCountStyleMobile: () => ({
    fontSize: 'var(--font-size-base)', // PHASE 12: Smaller count (was xl)
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    lineHeight: '1.2',
  }),
  metricLabel: {
    fontSize: 'var(--font-size-xs, 0.75rem)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-text-secondary, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  errorNote: {
    padding: '0 var(--spacing-4) var(--spacing-2)',
    marginTop: 'calc(-1 * var(--spacing-2))',
  },
  // PHASE 7A: Mobile error note (smaller, more subtle)
  getErrorNoteStyleMobile: () => ({
    padding: '0 var(--spacing-3) var(--spacing-1)',
    marginTop: 'calc(-1 * var(--spacing-1))',
  }),
  errorText: {
    fontSize: 'var(--font-size-xs, 0.75rem)',
    color: 'var(--color-status-error-text, #dc2626)',
    fontStyle: 'italic',
  },
  // PHASE 7A: Mobile error text (smaller, secondary color, less prominent)
  getErrorTextStyleMobile: () => ({
    fontSize: 'var(--font-size-xs, 0.625rem)', // PHASE 7A: Smaller on mobile
    color: 'var(--color-text-secondary, #6b7280)', // PHASE 7A: Secondary color (not error red)
    fontStyle: 'normal', // PHASE 7A: No italic on mobile
  }),
  // BEYOND WORLD-CLASS: Trust-grade status notice styles
  getStatusNoticeStyleMobile: () => ({
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    marginTop: 'var(--spacing-2)',
  }),
  statusNoticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    marginBottom: 'var(--spacing-2)',
  },
  statusNoticeIcon: {
    fontSize: 'var(--font-size-base)',
    flexShrink: 0,
  },
  statusNoticeTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  statusNoticeBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  statusNoticeText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.4,
    margin: 0,
  },
  statusNoticeAction: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    minHeight: '36px',
    transition: 'background-color 120ms ease-out',
  },
};
